import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, FileText, Printer, RefreshCw, Trash2, X, Download, Sparkles, Truck, PackageCheck, Undo2 } from 'lucide-react';
import {
  apiCreateDocument, apiListDocuments, apiUpdateDocument, apiDeleteDocument,
  apiListPartners, apiListProducts, apiOpenDocumentPdf, apiDownloadDocumentPdf, apiConvertDocument,
  apiListWarehouses, apiDdtAvailableForCustomer, apiConfirmDdtShipment, apiCancelDdtShipment
} from '../../erpApi';
import { toast } from 'sonner';

const DOC_LABEL = {
  quote: 'Preventivo', invoice: 'Fattura', ddt: 'DDT',
  credit_note: 'Nota di credito', customer_order: 'Ordine cliente', supplier_order: 'Ordine fornitore'
};
const DOC_COLOR = {
  quote: 'bg-slate-100 text-slate-700', invoice: 'bg-blue-100 text-blue-700',
  ddt: 'bg-amber-100 text-amber-700', credit_note: 'bg-red-100 text-red-700',
  customer_order: 'bg-emerald-100 text-emerald-700', supplier_order: 'bg-violet-100 text-violet-700'
};
const STATUS_COLOR = {
  draft: 'bg-slate-100 text-slate-600', confirmed: 'bg-blue-100 text-blue-700',
  sent: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700'
};

const emptyLine = () => ({
  _rid: `l${Date.now()}${Math.random()}`,
  product_id: '', description: '', quantity: 1, unit_price: 0, discount_pct: 0,
  vat_rate: 22, unit_of_measure: 'pz', lot_number: '',
  create_new_product: false,
  new_product_sku: '', new_product_name: '', new_product_kind: 'raw_material',
  new_product_category: '', new_product_barcode: '', new_product_min_stock: 0,
  new_product_customer_id: ''
});

const DocumentForm = ({ partners, products, warehouses, onSubmit, onCancel }) => {
  const [f, setF] = useState({
    doc_type: 'invoice', partner_id: '', date: new Date().toISOString().slice(0, 10),
    due_date: '', reference: '', notes: '', payment_method: 'Bonifico bancario',
    warehouse_id: '', location_id: '',
    // DDT fields
    causale_trasporto: 'C/LAVORAZIONE', vostro_riferimento: '',
    num_colli: '', num_pallet: '', aspetto_esteriore: 'PALLET',
    peso_lordo: '', peso_netto: '', vettore: '', porto: 'FRANCO',
    trasporto_a_cura_di: 'MITTENTE', transport_datetime: ''
  });
  const [lines, setLines] = useState([emptyLine()]);
  // DDT cliente: stato PF disponibili da OL completati
  const [pfAvailable, setPfAvailable] = useState([]);
  const [pfLoaded, setPfLoaded] = useState(false);
  const [loadingPf, setLoadingPf] = useState(false);
  const [ordersCount, setOrdersCount] = useState(0);

  const loadPfFromOrders = async () => {
    if (!f.partner_id || !f.warehouse_id) return;
    setLoadingPf(true);
    try {
      const res = await apiDdtAvailableForCustomer(f.partner_id, f.warehouse_id);
      const rows = (res.rows || []).map(r => ({ ...r, _selected: true, _qtyShip: r.qty_available }));
      setPfAvailable(rows);
      setOrdersCount(res.orders_count || 0);
      setPfLoaded(true);
      if (rows.length === 0) toast.info('Nessun PF disponibile per questo cliente nel magazzino selezionato');
      else toast.success(`${rows.length} PF disponibili da ${res.orders_count} OL`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento PF');
    } finally {
      setLoadingPf(false);
    }
  };

  const updatePfRow = (i, k, v) => setPfAvailable(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const addSelectedPf = () => {
    const selected = pfAvailable.filter(r => r._selected && parseFloat(r._qtyShip) > 0);
    if (selected.length === 0) return toast.error('Seleziona almeno una riga con quantità > 0');
    // Verifica che non superi available
    for (const r of selected) {
      if (parseFloat(r._qtyShip) > r.qty_available) {
        return toast.error(`${r.product_sku}: quantità ${r._qtyShip} > disponibile ${r.qty_available}`);
      }
    }
    const newLines = selected.map(r => ({
      _rid: `pf${r.product_id}-${r.source_order_id}-${Date.now()}${Math.random()}`,
      product_id: r.product_id, product_sku: r.product_sku,
      description: r.product_name, quantity: parseFloat(r._qtyShip),
      unit_price: r.sale_price || 0, discount_pct: 0,
      vat_rate: r.vat_rate || 22, unit_of_measure: r.unit_of_measure || 'pz',
      lot_number: r.lot_number || '',
      source_production_order_id: r.source_order_id,
      source_production_order_number: r.source_order_number,
      source_warehouse_id: r.warehouse_id,
      create_new_product: false,
      new_product_sku: '', new_product_name: '', new_product_kind: 'raw_material',
      new_product_category: '', new_product_barcode: '', new_product_min_stock: 0,
      new_product_customer_id: ''
    }));
    // Sostituisce righe vuote o appende
    setLines(prev => {
      const filtered = prev.filter(ln => ln.product_id || ln.description || ln.create_new_product);
      return [...filtered, ...newLines];
    });
    // Rimuove righe aggiunte dalla lista disponibili
    setPfAvailable(prev => prev.filter(r => !(r._selected && parseFloat(r._qtyShip) > 0)));
    toast.success(`${newLines.length} righe aggiunte al DDT`);
  };

  // Reset PF list se cambia cliente o magazzino
  useEffect(() => { setPfAvailable([]); setPfLoaded(false); }, [f.partner_id, f.warehouse_id]);

  // Partner selected
  const selectedPartner = useMemo(() => partners.find(p => p.id === f.partner_id), [f.partner_id, partners]);
  const isDDT = f.doc_type === 'ddt';
  const isSupplierDDT = isDDT && selectedPartner?.kind === 'supplier';
  const isCustomerDDT = isDDT && selectedPartner?.kind === 'customer';
  const hideEconomics = isDDT; // DDT è puramente logistico

  // Filter partners by type matching document
  const isCustomerDoc = ['quote', 'invoice', 'credit_note', 'customer_order'].includes(f.doc_type);
  const isSupplierDoc = ['supplier_order'].includes(f.doc_type);
  const partnersFiltered = useMemo(() => partners.filter(p => {
    if (isCustomerDoc) return p.kind === 'customer';
    if (isSupplierDoc) return p.kind === 'supplier';
    return true; // DDT può essere sia in che out
  }), [partners, isCustomerDoc, isSupplierDoc]);
  const customerPartners = useMemo(() => partners.filter(p => p.kind === 'customer'), [partners]);

  // Filter products by selected customer (req: "Lista prodotti filtrata per cliente nel DDT")
  const filteredProducts = useMemo(() => {
    if (!selectedPartner || selectedPartner.kind !== 'customer') return products;
    // Mostra solo prodotti associati al cliente; ammette prodotti legacy senza customer_id (null)
    return products.filter(p => !p.customer_id || p.customer_id === selectedPartner.id);
  }, [products, selectedPartner]);

  const addLine = () => setLines(p => [...p, emptyLine()]);
  const delLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));
  const updLine = (i, k, v) => setLines(p => p.map((ln, idx) => idx === i ? { ...ln, [k]: v } : ln));

  const onSelectProduct = (i, productId) => {
    if (productId === '__NEW__') {
      setLines(prev => prev.map((ln, idx) => idx === i ? {
        ...ln, product_id: '', create_new_product: true,
        new_product_customer_id: selectedPartner?.kind === 'customer' ? selectedPartner.id : ln.new_product_customer_id
      } : ln));
      return;
    }
    const p = products.find(x => x.id === productId);
    if (!p) return;
    setLines(prev => prev.map((ln, idx) => idx === i ? {
      ...ln, product_id: productId, create_new_product: false,
      description: p.name,
      unit_price: isSupplierDDT ? (p.purchase_price || ln.unit_price) : (p.sale_price || 0),
      vat_rate: p.vat_rate || 22, unit_of_measure: p.unit_of_measure || 'pz',
      lot_number: p.lot_number || ln.lot_number,
    } : ln));
  };

  const computeLine = (ln) => {
    const net = (parseFloat(ln.quantity) || 0) * (parseFloat(ln.unit_price) || 0) * (1 - (parseFloat(ln.discount_pct) || 0) / 100);
    const vat = net * (parseFloat(ln.vat_rate) || 0) / 100;
    return { net, vat, total: net + vat };
  };
  const totals = lines.reduce((a, ln) => {
    const c = computeLine(ln);
    a.subtotal += c.net; a.vat += c.vat; a.total += c.total;
    return a;
  }, { subtotal: 0, vat: 0, total: 0 });

  const submit = (e) => {
    e.preventDefault();
    if (!f.partner_id) return toast.error('Anagrafica obbligatoria');
    if (isSupplierDDT && !f.warehouse_id) return toast.error('Per DDT fornitore: magazzino destinazione obbligatorio (per carico automatico)');
    const clean = lines.filter(ln =>
      (ln.product_id || ln.create_new_product || ln.description) && parseFloat(ln.quantity) > 0
    );
    if (clean.length === 0) return toast.error('Aggiungi almeno una riga');
    // Validate wizard lines
    for (const ln of clean) {
      if (ln.create_new_product && (!ln.new_product_sku?.trim() || !ln.new_product_name?.trim())) {
        return toast.error('Nuovo prodotto: SKU e Nome obbligatori');
      }
    }
    onSubmit({
      ...f,
      // DDT numeric fields: empty string → null
      num_colli: f.num_colli === '' ? null : parseInt(f.num_colli, 10),
      num_pallet: f.num_pallet === '' ? null : parseInt(f.num_pallet, 10),
      peso_lordo: f.peso_lordo === '' ? null : parseFloat(f.peso_lordo),
      peso_netto: f.peso_netto === '' ? null : parseFloat(f.peso_netto),
      lines: clean.map(ln => ({
        product_id: ln.create_new_product ? null : (ln.product_id || null),
        description: ln.description || ln.new_product_name || '',
        quantity: parseFloat(ln.quantity),
        unit_price: parseFloat(ln.unit_price) || 0,
        discount_pct: parseFloat(ln.discount_pct) || 0,
        vat_rate: parseFloat(ln.vat_rate) || 0,
        unit_of_measure: ln.unit_of_measure || 'pz',
        lot_number: ln.lot_number || '',
        source_production_order_id: ln.source_production_order_id || null,
        source_production_order_number: ln.source_production_order_number || '',
        source_warehouse_id: ln.source_warehouse_id || null,
        create_new_product: !!ln.create_new_product,
        new_product_sku: ln.new_product_sku || '',
        new_product_name: ln.new_product_name || '',
        new_product_kind: ln.new_product_kind || 'raw_material',
        new_product_category: ln.new_product_category || '',
        new_product_barcode: ln.new_product_barcode || '',
        new_product_min_stock: parseFloat(ln.new_product_min_stock) || 0,
        new_product_customer_id: ln.new_product_customer_id || null,
      }))
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="doc-form">
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label>Tipo documento *</Label>
          <Select value={f.doc_type} onValueChange={(v) => setF({ ...f, doc_type: v })}>
            <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label>Scadenza</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5"><Label>Anagrafica *</Label>
        <Select value={f.partner_id} onValueChange={(v) => setF({ ...f, partner_id: v })}>
          <SelectTrigger data-testid="doc-partner"><SelectValue placeholder={isCustomerDoc ? 'Cliente...' : isSupplierDoc ? 'Fornitore...' : 'Cliente / Fornitore...'} /></SelectTrigger>
          <SelectContent>{partnersFiltered.map(p => <SelectItem key={p.id} value={p.id}>[{p.code}] {p.business_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Anteprima dati cliente (auto-fill) */}
      {selectedPartner && (
        <div className="p-2.5 bg-slate-50 border border-slate-200 rounded text-xs space-y-0.5" data-testid="partner-preview">
          <div className="font-semibold text-slate-700">{selectedPartner.business_name}</div>
          <div className="text-slate-500">{selectedPartner.address || '—'}</div>
          <div className="text-slate-500">{selectedPartner.zip_code} {selectedPartner.city} {selectedPartner.province ? `(${selectedPartner.province})` : ''} — {selectedPartner.country || 'IT'}</div>
          <div className="flex gap-3 text-slate-500">
            <span><b>P.IVA:</b> {selectedPartner.vat_number || '—'}</span>
            <span><b>C.F.:</b> {selectedPartner.fiscal_code || '—'}</span>
            {selectedPartner.sdi_code && <span><b>SDI:</b> {selectedPartner.sdi_code}</span>}
          </div>
        </div>
      )}

      {/* DDT cliente: magazzino origine + bottone caricamento PF da OL completati */}
      {isCustomerDDT && (
        <div className="p-3 bg-slate-50 border border-slate-300 rounded space-y-2" data-testid="ddt-customer-section">
          <div className="flex items-center gap-2 text-slate-900 text-xs font-semibold">
            <PackageCheck className="w-4 h-4" /> DDT CLIENTE — Carica Prodotti Finiti da OL completati
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-2"><Label className="text-xs">Magazzino origine PF *</Label>
              <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
                <SelectTrigger data-testid="ddt-cust-warehouse" className="h-9"><SelectValue placeholder="Seleziona magazzino di prelievo..." /></SelectTrigger>
                <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                type="button" variant="outline" size="sm"
                data-testid="ddt-load-pf"
                disabled={!f.warehouse_id || loadingPf}
                onClick={loadPfFromOrders}
                className="w-full h-9 gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5" />{loadingPf ? 'Caricamento...' : 'Carica PF disponibili'}
              </Button>
            </div>
          </div>
          {pfAvailable.length > 0 && (
            <div className="text-[11px] text-slate-700">
              <b>{pfAvailable.length} PF disponibili</b> da {ordersCount} OL completato/i.
              Seleziona quelli da spedire e modifica le quantità.
            </div>
          )}
          {pfAvailable.length > 0 && (
            <div className="max-h-[260px] overflow-y-auto border border-slate-200 rounded bg-white" data-testid="pf-available-list">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 w-8"></th>
                    <th className="text-left px-2 py-1.5">SKU</th>
                    <th className="text-left px-2 py-1.5">Descrizione</th>
                    <th className="text-left px-2 py-1.5">OL</th>
                    <th className="text-left px-2 py-1.5">Lotto</th>
                    <th className="text-right px-2 py-1.5">Disponibile</th>
                    <th className="text-right px-2 py-1.5 w-24">Da spedire</th>
                  </tr>
                </thead>
                <tbody>
                  {pfAvailable.map((row, i) => (
                    <tr key={`${row.product_id}-${row.lot_number}-${row.source_order_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5"><input type="checkbox" checked={!!row._selected} onChange={(e) => updatePfRow(i, '_selected', e.target.checked)} /></td>
                      <td className="px-2 py-1.5 font-mono">{row.product_sku}</td>
                      <td className="px-2 py-1.5">{row.product_name}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-500">{row.source_order_number}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-500">{row.lot_number || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{row.qty_available} {row.unit_of_measure}</td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" min="0" step="0.001"
                          max={row.qty_available}
                          value={row._qtyShip}
                          onChange={(e) => updatePfRow(i, '_qtyShip', e.target.value)}
                          className="h-7 text-xs text-right"
                          data-testid={`pf-qty-${row.product_sku}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 border-t border-slate-200 flex justify-between items-center bg-slate-50">
                <span className="text-[11px] text-slate-600">Seleziona ✔ le righe per aggiungerle al DDT</span>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addSelectedPf} data-testid="pf-add-to-ddt">
                  ➕ Aggiungi {pfAvailable.filter(r => r._selected).length} al DDT
                </Button>
              </div>
            </div>
          )}
          {pfLoaded && pfAvailable.length === 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              ⓘ Nessun PF disponibile per questo cliente. Verifica che ci siano OL nello stato "Completato" o "Chiuso" con versamenti a magazzino.
            </div>
          )}
        </div>
      )}

      {/* DDT fornitore: warehouse destinazione */}
      {isSupplierDDT && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded space-y-2">
          <div className="flex items-center gap-2 text-emerald-900 text-xs font-semibold">
            <Truck className="w-4 h-4" /> DDT FORNITORE — Carico magazzino automatico
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Magazzino destinazione *</Label>
              <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
                <SelectTrigger data-testid="doc-warehouse" className="h-9"><SelectValue placeholder="Seleziona magazzino..." /></SelectTrigger>
                <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-[11px] text-emerald-700">All'invio le righe del DDT genereranno carichi di magazzino con i lotti indicati. Prodotti nuovi potranno essere creati al volo.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Riferimento</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="es. Ordine cliente n°..." /></div>
        {!hideEconomics && (
          <div className="space-y-1.5"><Label>Modalità pagamento</Label><Input value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} /></div>
        )}
      </div>

      {/* === DATI TRASPORTO (visibili solo per DDT) === */}
      {isDDT && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded space-y-2.5" data-testid="ddt-transport-section">
          <div className="flex items-center gap-2 text-blue-900 text-xs font-semibold">
            <Truck className="w-4 h-4" /> DATI TRASPORTO (Documento di Trasporto — D.P.R. 472/96)
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-[11px]">Causale trasporto</Label>
              <Select value={f.causale_trasporto} onValueChange={(v) => setF({ ...f, causale_trasporto: v })}>
                <SelectTrigger data-testid="ddt-causale" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="C/LAVORAZIONE">C/LAVORAZIONE</SelectItem>
                  <SelectItem value="VENDITA">VENDITA</SelectItem>
                  <SelectItem value="RESO">RESO</SelectItem>
                  <SelectItem value="CAMPIONATURA">CAMPIONATURA</SelectItem>
                  <SelectItem value="C/VISIONE">C/VISIONE</SelectItem>
                  <SelectItem value="C/RIPARAZIONE">C/RIPARAZIONE</SelectItem>
                  <SelectItem value="TRASFERIMENTO">TRASFERIMENTO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Vostro Riferimento</Label>
              <Input data-testid="ddt-vostro-rif" value={f.vostro_riferimento} onChange={(e) => setF({ ...f, vostro_riferimento: e.target.value })} className="h-8 text-xs" placeholder="es. Ordine cliente..." />
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Data e Ora trasporto</Label>
              <Input data-testid="ddt-transport-dt" type="datetime-local" value={f.transport_datetime} onChange={(e) => setF({ ...f, transport_datetime: e.target.value })} className="h-8 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2">
            <div className="space-y-1"><Label className="text-[11px]">Aspetto esteriore</Label>
              <Select value={f.aspetto_esteriore} onValueChange={(v) => setF({ ...f, aspetto_esteriore: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PALLET">PALLET</SelectItem>
                  <SelectItem value="CARTONI">CARTONI</SelectItem>
                  <SelectItem value="CASSE">CASSE</SelectItem>
                  <SelectItem value="SFUSO">SFUSO</SelectItem>
                  <SelectItem value="A VISTA">A VISTA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">N° Colli</Label>
              <Input data-testid="ddt-colli" type="number" min="0" value={f.num_colli} onChange={(e) => setF({ ...f, num_colli: e.target.value })} className="h-8 text-xs" />
            </div>
            <div className="space-y-1"><Label className="text-[11px]">N° Pallet</Label>
              <Input data-testid="ddt-pallet" type="number" min="0" value={f.num_pallet} onChange={(e) => setF({ ...f, num_pallet: e.target.value })} className="h-8 text-xs" />
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Peso Lordo (kg)</Label>
              <Input data-testid="ddt-peso-lordo" type="number" step="0.01" min="0" value={f.peso_lordo} onChange={(e) => setF({ ...f, peso_lordo: e.target.value })} className="h-8 text-xs" />
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Peso Netto (kg)</Label>
              <Input data-testid="ddt-peso-netto" type="number" step="0.01" min="0" value={f.peso_netto} onChange={(e) => setF({ ...f, peso_netto: e.target.value })} className="h-8 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-[11px]">Vettore</Label>
              <Input data-testid="ddt-vettore" value={f.vettore} onChange={(e) => setF({ ...f, vettore: e.target.value })} className="h-8 text-xs" placeholder="Ragione sociale vettore" />
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Porto</Label>
              <Select value={f.porto} onValueChange={(v) => setF({ ...f, porto: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FRANCO">FRANCO</SelectItem>
                  <SelectItem value="ASSEGNATO">ASSEGNATO</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-[11px]">Trasporto a cura di</Label>
              <Select value={f.trasporto_a_cura_di} onValueChange={(v) => setF({ ...f, trasporto_a_cura_di: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MITTENTE">MITTENTE</SelectItem>
                  <SelectItem value="DESTINATARIO">DESTINATARIO</SelectItem>
                  <SelectItem value="VETTORE">VETTORE</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between"><Label>Righe *</Label><Button type="button" size="sm" variant="outline" onClick={addLine} className="h-8"><Plus className="w-3 h-3 mr-1" />Riga</Button></div>
        {lines.map((ln, idx) => (
          <div key={ln._rid} className={`p-2 border rounded space-y-1 ${ln.create_new_product ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`} data-testid={`doc-line-${idx}`}>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-5">
                <Select value={ln.create_new_product ? '__NEW__' : (ln.product_id || '')} onValueChange={(v) => onSelectProduct(idx, v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Prodotto (opzionale)" /></SelectTrigger>
                  <SelectContent>
                    {isSupplierDDT && <SelectItem value="__NEW__"><span className="text-amber-700 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" />➕ Nuovo prodotto (wizard)</span></SelectItem>}
                    {filteredProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-6"><Input value={ln.description} onChange={(e) => updLine(idx, 'description', e.target.value)} placeholder="Descrizione" className="h-8 text-xs" /></div>
              <div className="col-span-1"><Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => delLine(idx)}><X className="w-4 h-4" /></Button></div>
            </div>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-2"><Input type="number" step="0.001" value={ln.quantity} onChange={(e) => updLine(idx, 'quantity', e.target.value)} placeholder="Q.tà" className="h-8 text-xs" /></div>
              <div className="col-span-1"><Input value={ln.unit_of_measure} onChange={(e) => updLine(idx, 'unit_of_measure', e.target.value)} placeholder="U.M." className="h-8 text-xs" /></div>
              {!hideEconomics && <div className="col-span-2"><Input type="number" step="0.01" value={ln.unit_price} onChange={(e) => updLine(idx, 'unit_price', e.target.value)} placeholder="€ unit." className="h-8 text-xs" /></div>}
              {!hideEconomics && <div className="col-span-1"><Input type="number" step="0.01" value={ln.discount_pct} onChange={(e) => updLine(idx, 'discount_pct', e.target.value)} placeholder="Sc%" className="h-8 text-xs" /></div>}
              {!hideEconomics && <div className="col-span-1"><Input type="number" step="0.01" value={ln.vat_rate} onChange={(e) => updLine(idx, 'vat_rate', e.target.value)} placeholder="IVA%" className="h-8 text-xs" /></div>}
              <div className={hideEconomics ? 'col-span-9' : 'col-span-3'}><Input value={ln.lot_number} onChange={(e) => updLine(idx, 'lot_number', e.target.value)} placeholder="Lotto" className="h-8 text-xs font-mono" /></div>
              {!hideEconomics && <div className="col-span-2 flex items-center justify-end text-xs font-semibold text-blue-700">€{computeLine(ln).net.toFixed(2)}</div>}
            </div>
            {ln.create_new_product && (
              <div className="mt-2 p-2 border border-amber-300 bg-white rounded space-y-2" data-testid={`wizard-product-${idx}`}>
                <div className="text-[11px] font-semibold text-amber-800 flex items-center gap-1"><Sparkles className="w-3 h-3" />Wizard nuovo prodotto</div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3"><Input value={ln.new_product_sku} onChange={(e) => updLine(idx, 'new_product_sku', e.target.value.toUpperCase())} placeholder="SKU *" className="h-8 text-xs font-mono uppercase" /></div>
                  <div className="col-span-5"><Input value={ln.new_product_name} onChange={(e) => updLine(idx, 'new_product_name', e.target.value)} placeholder="Nome prodotto *" className="h-8 text-xs" /></div>
                  <div className="col-span-2">
                    <Select value={ln.new_product_kind} onValueChange={(v) => updLine(idx, 'new_product_kind', v)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="raw_material">Materia prima</SelectItem>
                        <SelectItem value="semi_finished">Semilavorato</SelectItem>
                        <SelectItem value="finished">Prodotto finito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input value={ln.new_product_category} onChange={(e) => updLine(idx, 'new_product_category', e.target.value)} placeholder="Categoria" className="h-8 text-xs" /></div>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3"><Input value={ln.new_product_barcode} onChange={(e) => updLine(idx, 'new_product_barcode', e.target.value)} placeholder="Barcode (EAN)" className="h-8 text-xs font-mono" /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" value={ln.new_product_min_stock} onChange={(e) => updLine(idx, 'new_product_min_stock', e.target.value)} placeholder="Soglia min" className="h-8 text-xs" /></div>
                  <div className="col-span-5">
                    <Select value={ln.new_product_customer_id || ''} onValueChange={(v) => updLine(idx, 'new_product_customer_id', v || null)}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Cliente proprietario (opzionale)" /></SelectTrigger>
                      <SelectContent>
                        {customerPartners.map(p => <SelectItem key={p.id} value={p.id}>{p.business_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 text-[10px] text-amber-700 self-center">Verrà creato a catalogo</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!hideEconomics && (
        <div className="grid grid-cols-3 gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
          <div><div className="text-[10px] uppercase text-slate-500">Imponibile</div><div className="font-semibold tabular-nums">€{totals.subtotal.toFixed(2)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500">IVA</div><div className="font-semibold tabular-nums">€{totals.vat.toFixed(2)}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500">Totale</div><div className="font-bold text-blue-700 tabular-nums text-base">€{totals.total.toFixed(2)}</div></div>
        </div>
      )}
      {hideEconomics && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
          ⓘ Il DDT è un documento logistico. Prezzi, sconti, IVA e totali non vengono visualizzati né stampati.
        </div>
      )}

      <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
      <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Annulla</Button><Button data-testid="doc-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">Crea documento</Button></DialogFooter>
    </form>
  );
};

const DocumentsManager = () => {
  const [list, setList] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const reload = async () => {
    const params = typeFilter !== 'all' ? { doc_type: typeFilter } : {};
    const [d, p, pr, wh] = await Promise.all([
      apiListDocuments(params), apiListPartners(), apiListProducts(), apiListWarehouses()
    ]);
    setList(d); setPartners(p); setProducts(pr); setWarehouses(wh);
  };
  useEffect(() => { reload(); }, [typeFilter]); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      const res = await apiCreateDocument(data);
      if (res.auto_stock_loaded) toast.success(`Documento creato · Carico magazzino effettuato`);
      else toast.success('Documento creato');
      setDialogOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const convert = async (d, target) => {
    try { await apiConvertDocument(d.id, target); toast.success('Convertito'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const del = async (d) => {
    if (!window.confirm(`Eliminare ${d.number}?`)) return;
    try { await apiDeleteDocument(d.id); toast.success('Eliminato'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const updateStatus = async (d, status) => {
    try { await apiUpdateDocument(d.id, { status }); toast.success('Stato aggiornato'); reload(); }
    catch { toast.error('Errore'); }
  };
  const openPdf = async (d) => {
    try { await apiOpenDocumentPdf(d.id); }
    catch { toast.error('Errore apertura PDF'); }
  };
  const [confirmingShipment, setConfirmingShipment] = useState(null); // id del DDT in corso di conferma
  const confirmShipment = async (d) => {
    if (d.shipment_confirmed_at || confirmingShipment === d.id) return;
    if (!window.confirm(`Confermare DDT ${d.number} e scaricare il magazzino?\nDopo la conferma, il DDT non sarà più modificabile.`)) return;
    setConfirmingShipment(d.id);
    try {
      const res = await apiConfirmDdtShipment(d.id);
      toast.success(res.message || 'Magazzino scaricato');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore conferma');
    } finally {
      setConfirmingShipment(null);
    }
  };
  const cancelShipment = async (d) => {
    if (!window.confirm(`Annullare DDT ${d.number}?\nLe giacenze verranno ripristinate (movimento reverse).`)) return;
    try {
      const res = await apiCancelDdtShipment(d.id);
      toast.success(res.message || 'DDT annullato');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore annullamento'); }
  };
  return (
    <div className="space-y-3" data-testid="documents-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="doc-type-filter" className="h-9 w-[200px] text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {Object.entries(DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="doc-new" className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo documento</Button></DialogTrigger>
          <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuovo documento</DialogTitle><DialogDescription>Preventivo, fattura, DDT, NC, ordini. Per DDT fornitore: carico magazzino automatico.</DialogDescription></DialogHeader>
            <DocumentForm partners={partners} products={products} warehouses={warehouses} onSubmit={handleSubmit} onCancel={() => setDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun documento</div></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Tipo</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Numero</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Data</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Anagrafica</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Imponibile</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Totale</th>
                <th className="text-center px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Stato</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold uppercase text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map(d => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${DOC_COLOR[d.doc_type]}`}>{DOC_LABEL[d.doc_type]}</span></td>
                  <td className="px-3 py-2 font-mono font-semibold text-blue-700">{d.number}</td>
                  <td className="px-3 py-2 text-xs">{d.date}</td>
                  <td className="px-3 py-2"><span className="font-mono text-xs text-slate-500 mr-1">{d.partner_code}</span>{d.partner_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs">€{(d.subtotal || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">€{(d.total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    <Select value={d.status} onValueChange={(v) => updateStatus(d, v)}>
                      <SelectTrigger className={`h-6 text-[10px] w-[100px] mx-auto border-0 ${STATUS_COLOR[d.status]}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Bozza</SelectItem>
                        <SelectItem value="confirmed">Confermato</SelectItem>
                        <SelectItem value="sent">Inviato</SelectItem>
                        <SelectItem value="paid">Pagato</SelectItem>
                        <SelectItem value="cancelled">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {d.doc_type === 'ddt' && d.status !== 'cancelled' && !d.shipment_confirmed_at && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        disabled={confirmingShipment === d.id}
                        title={confirmingShipment === d.id ? 'Conferma in corso...' : 'Conferma e scarica magazzino'}
                        data-testid={`ddt-confirm-${d.number}`} onClick={() => confirmShipment(d)}>
                        <PackageCheck className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {d.doc_type === 'ddt' && d.shipment_confirmed_at && d.status !== 'cancelled' && (
                      <span className="text-[10px] uppercase font-semibold text-emerald-700 mr-1 px-1.5 py-0.5 bg-emerald-50 rounded" title={`Confermato il ${new Date(d.shipment_confirmed_at).toLocaleString('it-IT')}`} data-testid={`ddt-confirmed-${d.number}`}>
                        ✓ Confermato
                      </span>
                    )}
                    {d.doc_type === 'ddt' && d.shipment_confirmed_at && d.status !== 'cancelled' && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-orange-700" title="Annulla DDT e reverse magazzino"
                        data-testid={`ddt-cancel-${d.number}`} onClick={() => cancelShipment(d)}>
                        <Undo2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Apri PDF" data-testid={`doc-open-${d.number}`} onClick={() => openPdf(d)}><Printer className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Scarica PDF" data-testid={`doc-download-${d.number}`} onClick={() => apiDownloadDocumentPdf(d.id, `${d.number}.pdf`)}><Download className="w-3.5 h-3.5" /></Button>
                    {d.doc_type === 'quote' && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Converti in fattura" onClick={() => convert(d, 'invoice')}><RefreshCw className="w-3.5 h-3.5" /></Button>}
                    {d.doc_type === 'ddt' && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Converti in fattura" onClick={() => convert(d, 'invoice')}><RefreshCw className="w-3.5 h-3.5" /></Button>}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => del(d)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentsManager;
